/**
 * useCommandAutocomplete
 *
 * Command-autocomplete dropdown state extracted verbatim from ChatBox.tsx
 * (Stage 3). Owns the three pieces of dropdown state and returns every
 * value + setter the orchestrator still reads/writes (keyboard nav, the
 * dropdown JSX, and the input-change matching logic).
 *
 * Behavior is byte-identical to the in-line implementation:
 *  - three plain useState calls in the same order/initial values,
 *  - no effects, no deps, no inputs — a pure state container,
 *  - the matching logic (getMatchingCommands) stays in the orchestrator and
 *    drives these setters exactly as before.
 */
import { useState } from 'react';
import type { CommandInfo } from '@/features/chat/utils/command-parser';

export interface UseCommandAutocompleteReturn {
  showCommandDropdown: boolean;
  setShowCommandDropdown: React.Dispatch<React.SetStateAction<boolean>>;
  commandMatches: CommandInfo[];
  setCommandMatches: React.Dispatch<React.SetStateAction<CommandInfo[]>>;
  selectedCommandIndex: number;
  setSelectedCommandIndex: React.Dispatch<React.SetStateAction<number>>;
}

export function useCommandAutocomplete(): UseCommandAutocompleteReturn {
  // Command autocomplete state
  const [showCommandDropdown, setShowCommandDropdown] = useState(false);
  const [commandMatches, setCommandMatches] = useState<CommandInfo[]>([]);
  const [selectedCommandIndex, setSelectedCommandIndex] = useState(0);

  return {
    showCommandDropdown,
    setShowCommandDropdown,
    commandMatches,
    setCommandMatches,
    selectedCommandIndex,
    setSelectedCommandIndex,
  };
}
